import { ICellRendererParams, IRowNode, ValueFormatterParams } from 'ag-grid-community'
import { first, intersection, isEmpty, isEqual, isNil, last } from 'lodash-es'
import {
  AuditDetail,
  NegotiationDetail,
  NegotiationEvent,
  NegotiationOrAuditDetail,
  NegotiationType,
  OrderDetail,
  OrderDetailSummary,
  RecursiveNullable,
  SummaryRowDirection,
  TradeConditionFlag
} from 'api'
import {
  GroupRowData,
  TypedCellRendererParams,
  TypedContext,
  TypedEditableCallbackParams,
  TypedRowNode,
  TypedValueFormatterParams,
  TypedValueGetterParams
} from 'view/components/datagrid'
import { CollectionColumnId, ContainerCollectionColumnId } from 'view/routes/ColumnNames'
import { hasNegotiations, isAONOrSmartGroupSummary } from 'view/components/datagrid/renderers/negotiationDataUtil'
import { getBestQuoteReceived } from 'view/routes/requests/shared/utils'
import { GridId } from 'store/grid/gridTypes'
import { DEFAULT_INITIATED_SUMMARY_MAP_KEY } from 'selectors/negotiations/constants'
import { isNotNil } from 'utils/predicate'

export type RowDataGetter<T, S = T> = (
  params:
    | TypedValueGetterParams<T, S>
    | ValueFormatterParams
    | TypedEditableCallbackParams<T, S>
    | TypedCellRendererParams<T>
    | ICellRendererParams,
  useNodeData?: boolean
) => T | S | null

export const stagingGroupRowDataGetter = (
  node: TypedRowNode<OrderDetail>,
  groupRowData?: GroupRowData<OrderDetail | OrderDetailSummary>
): OrderDetail | OrderDetailSummary | null => {
  if (
    node.group &&
    node.rowGroupColumn?.getColId() === CollectionColumnId.COLLECTION_NAME &&
    groupRowData?.[CollectionColumnId.COLLECTION_NAME]
  ) {
    const collectionGroupRowData = groupRowData?.[CollectionColumnId.COLLECTION_NAME]
    const firstChildCollectionId = node.allLeafChildren[0]?.data?.collection?.id

    if (collectionGroupRowData && firstChildCollectionId) {
      return collectionGroupRowData[firstChildCollectionId]
    }
  }

  if (
    node.group &&
    node.rowGroupColumn?.getColId() === ContainerCollectionColumnId.CONTAINER_COLLECTION_NAME &&
    groupRowData?.[ContainerCollectionColumnId.CONTAINER_COLLECTION_NAME]
  ) {
    const containerGroupRowData = groupRowData?.[ContainerCollectionColumnId.CONTAINER_COLLECTION_NAME]
    const firstChildContainerId = node.allLeafChildren[0]?.data?.containerCollection?.id

    if (containerGroupRowData && firstChildContainerId) {
      return containerGroupRowData[firstChildContainerId]
    }
  }

  return null
}

export function getNegotiationSummary<T extends Exclude<NegotiationOrAuditDetail, AuditDetail>>(
  summaryNegotiationId: string,
  params: {
    node: IRowNode<T>
    context: TypedContext<T>
  }
): T | null {
  if (params.context.groupRowData) {
    const [summariesBySummaryId] = Object.values(params.context.groupRowData)

    const foundSummary = summariesBySummaryId[summaryNegotiationId]

    return foundSummary
  }

  return null
}

export const stagingRowDataGetter = (
  params: TypedValueGetterParams<OrderDetail, OrderDetailSummary> | TypedEditableCallbackParams<OrderDetail | OrderDetailSummary>,
  useNodeData?: boolean
): OrderDetail | OrderDetailSummary | null => {
  const groupRowData = stagingGroupRowDataGetter(params.node as TypedRowNode<OrderDetail>, params.context.groupRowData)
  return groupRowData ?? (useNodeData ? params.node.data : params.data) ?? null
}

function getGroupRowDataKeyArray(rowGroupColIds: string[], groupRowData: GroupRowData<NegotiationOrAuditDetail>): string[] | undefined {
  const groupRowDataKeyArrays = Object.keys(groupRowData).map(groupRowDataKey => groupRowDataKey.split('-'))

  // The columns that are currently grouped by should contain the relevant group row data key columns and be in the same order
  return groupRowDataKeyArrays.find(groupRowDataKeyArray =>
    isEqual(intersection(rowGroupColIds, groupRowDataKeyArray), groupRowDataKeyArray)
  )
}

export function negotiationSummaryRowDataGetter<T extends Exclude<NegotiationOrAuditDetail, AuditDetail>>(
  node: TypedRowNode<T>,
  rowGroupColIds: string[],
  context: TypedContext<T>,
  counterpartyId: string | undefined
): T | RecursiveNullable<T> | null {
  const { groupRowData } = context
  if (!node.group || rowGroupColIds.length === 0 || !node.rowGroupColumn || !groupRowData) {
    return null
  }

  const groupRowDataKeyArray = getGroupRowDataKeyArray(rowGroupColIds, groupRowData)
  const collectionGroupRowData = groupRowDataKeyArray && groupRowData[groupRowDataKeyArray.join('-')]

  if (context.gridId === GridId.InitiatedGrid && isContainerCollectionGroupNode(node)) {
    const summary: any = getContainerCollectionSummary(node, context)

    return summary ?? null
  }

  // Initiated Grid Collection Level AON Summary Row
  if (context.gridId === GridId.InitiatedGrid && isCollectionGroupNode(node)) {
    const summary = getBestPricedSummaryNegotiationDetailForCollectionNode(node, context)
    return isNotNil(summary) && isAONOrSmartGroupSummary(summary)
      ? {
          ...summary,
          meta: { isCollectionSummary: true }
        }
      : null
  }

  // Only return summary row data if the current row group is the lowest level colId in the group row data key array

  if (
    collectionGroupRowData &&
    groupRowData[groupRowDataKeyArray.join('-')] &&
    node.rowGroupColumn.getColId() === last(groupRowDataKeyArray)
  ) {
    const firstChildData = first(node.allLeafChildren)?.data
    if (isNil(firstChildData)) {
      return null
    }

    const firstChildSummaryNegotiationId =
      hasNegotiations(firstChildData) && counterpartyId && firstChildData.negotiations[counterpartyId]
        ? firstChildData.negotiations[counterpartyId].negotiation.summaryNegotiationId
        : firstChildData.negotiation.summaryNegotiationId

    if (firstChildSummaryNegotiationId && collectionGroupRowData[firstChildSummaryNegotiationId]) {
      const summaryRow = collectionGroupRowData[firstChildSummaryNegotiationId]
      return summaryDecorator(summaryRow, node.allLeafChildren)
    }
  }
  return null
}

function summaryDecorator<T extends Exclude<NegotiationOrAuditDetail, AuditDetail>>(summaryRow: T, children: TypedRowNode<T>[]): T {
  return {
    ...summaryRow,
    negotiation: {
      ...summaryRow.negotiation,
      summaryDirection: !children.every(childRow => childRow.data?.negotiation.direction === children[0].data?.negotiation.direction)
        ? SummaryRowDirection.MULTIPLE
        : summaryRow.negotiation.direction
    }
  }
}

export type NegotiationGetterParams<T extends Exclude<NegotiationOrAuditDetail, AuditDetail>> =
  | TypedValueGetterParams<T>
  | TypedValueFormatterParams<T, unknown, T>
  | TypedEditableCallbackParams<T>
  | TypedCellRendererParams<T>

export function negotiationRowDataGetter<T extends Exclude<NegotiationOrAuditDetail, AuditDetail>>(
  params: NegotiationGetterParams<T>,
  useNodeData?: boolean
): T | null {
  const summaryRowData = params.node
    ? negotiationSummaryRowDataGetter<T>(
        params.node as TypedRowNode<T>,
        params.api?.getRowGroupColumns()?.map(rowGroupColumn => rowGroupColumn.getColId()) ?? [],
        params.context,
        params.colDef?.cellRendererParams?.counterpartyId
      )
    : null
  const returnData = summaryRowData ?? (useNodeData ? params.node?.data : params.data)
  return isNil(returnData) || isEmpty(returnData) ? null : returnData
}

const isCollectionGroupNode = (node: IRowNode) =>
  node.group ? node.rowGroupColumn?.getColId() === CollectionColumnId.COLLECTION_NAME : false

const isContainerCollectionGroupNode = (node: IRowNode) => {
  return node.group ? node.rowGroupColumn?.getColId() === ContainerCollectionColumnId.CONTAINER_COLLECTION_NAME : false
}

const getBestPricedSummaryNegotiationDetailForCollectionNode = <T extends Exclude<NegotiationOrAuditDetail, AuditDetail>>(
  node: IRowNode<T>,
  context: TypedContext<T>
): T | null => {
  const allLeafChildren = node.allLeafChildren ?? []

  const collectionId = first(allLeafChildren.map(d => d.data?.negotiation.collectionId))
  const negotiationsToCompare =
    context.groupRowData && context.groupRowData[DEFAULT_INITIATED_SUMMARY_MAP_KEY]
      ? Object.values(context.groupRowData[DEFAULT_INITIATED_SUMMARY_MAP_KEY]).filter(
          n => n.negotiation.collectionId && n.negotiation.collectionId === collectionId
        )
      : []

  return getBestQuoteReceived(negotiationsToCompare) ?? null
}

export const getContainerCollectionSummary = <T extends Exclude<NegotiationOrAuditDetail, AuditDetail>>(
  node: IRowNode<T>,
  context: TypedContext<T>
): RecursiveNullable<NegotiationDetail> | null => {
  const allLeafChildren = node.allLeafChildren ?? []

  const containerCollectionId = first(allLeafChildren.map(d => d.data?.negotiation.containerCollectionId))

  const items =
    context.groupRowData && context.groupRowData[DEFAULT_INITIATED_SUMMARY_MAP_KEY]
      ? Object.values(context.groupRowData[DEFAULT_INITIATED_SUMMARY_MAP_KEY]).filter(
          n =>
            n.negotiation.negotiationType === NegotiationType.AON_X_SUMMARY && n.negotiation.containerCollectionId === containerCollectionId
        )
      : []

  function createNegotiationField<T>(value: T) {
    return {
      initiatorLastValue: value,
      modified: false,
      modifiedValue: null,
      negotiated: value,
      original: value,
      responderLastValue: value
    }
  }

  return {
    negotiation: {
      absoluteConsideration: null,
      basisConvention: null,
      accruedInterest: null,
      accruedInterestAmount: null,
      allInPrice: null,
      allowPartial: false,
      axeId: null,
      basketAllocation: null,
      basketId: null,
      batchId: '',
      blockTrade: false,
      bondPrice: createNegotiationField(null),
      bundle: null,
      calculatedStartConsideration: null,
      callablePeriodDays: null,
      callableType: null,
      canSendBackToStaging: false,
      cleanPrice: null,
      clearingHouseId: null,
      clearingType: null,
      collateralId: null,
      collectionId: null,
      comments: null,
      competitionCount: null,
      containerCollectionId: containerCollectionId ? containerCollectionId : null,
      counterpartyAlias: null,
      counterpartyClearingType: null,
      counterpartyIdNegotiableField: createNegotiationField(null),
      counterpartyPrimaryCoverUserId: null,
      counterpartySecondaryCoverUserId: null,
      counterpartySponsorId: createNegotiationField(null),
      counterpartyTraderId: null,
      createdAt: null,
      createdBy: null,
      creatingCorrelationId: '',
      csd: null,
      currency: null,
      dealId: null,
      direction: null,
      dirtyPrice: null,
      endConsideration: null,
      endDate: createNegotiationField(null),
      externalNegotiationId: null,
      externalOrderId: null,
      externalQuoteId: null,
      externalQuoteResponseId: null,
      externalSummaryNegotiationId: null,
      fee: null,
      floatCrystallisationDays: null,
      fundId: '',
      fundPrimaryCoverUserId: null,
      fundSecondaryCoverUserId: null,
      haircut: createNegotiationField(null),
      haircutType: createNegotiationField(null),
      hideCompetition: null,
      initiatorBook: createNegotiationField(null),
      initiatorExecutionDecisionMaker: createNegotiationField(null),
      initiatorInvestmentDecisionMaker: createNegotiationField(null),
      initiatorOrganisationId: '',
      initiatorPrimeBrokerId: null,
      initiatorPrimeBrokerName: null,
      initiatorStrategy: createNegotiationField(null),
      internalAxeId: null,
      isAxed: createNegotiationField(null),
      isCurve: null,
      isSponsorCounterparty: null,
      lastInitiatorId: null,
      lastInitiatorInitials: null,
      lastQuoted: null,
      lastQuotedAt: null,
      lastResponderId: null,
      lastResponderInitials: null,
      lastUpdatedAt: null,
      lastUpdatedAtSystem: null,
      lastUpdatedBy: null,
      market: null,
      marketType: null,
      marketIdentifierCode: null,
      masterLegalAgreementType: null,
      modified: false,
      negotiationId: '',
      negotiationMeta: {
        childNegotiationIds: [],
        childFundIds: [],
        childCollateralIds: [],
        childBasketIds: [],
        childSecondaryBasketIds: [],
        childSalesCoverIds: [],
        usAmount: 0,
        euAmount: 0
      },
      negotiationType: NegotiationType.AON_X_SUMMARY,
      notesId: null,
      noticePeriodDays: null,
      notional: createNegotiationField(null),
      otwEndTime: null,
      otwStartTime: null,
      parentOrderId: '',
      positionId: null,
      preAgreed: createNegotiationField(null),
      productId: null,
      productSectorId: null,
      rate: createNegotiationField(null),
      referenceRate: null,
      refresh: null,
      repoClassification: null,
      repoInterest: null,
      repoType: null,
      responderBook: createNegotiationField(null),
      responderExecutionDecisionMaker: createNegotiationField(null),
      responderInvestmentDecisionMaker: createNegotiationField(null),
      responderStrategy: createNegotiationField(null),
      rfqEndTime: '',
      rfqStartTime: '',
      rfqType: createNegotiationField(null),
      secondaryBasketId: null,
      secondaryCollateralId: null,
      sentBackToStaging: false,
      servicingTrigger: null,
      settlementVenueNegotiable: createNegotiationField(null),
      sponsorId: null,
      startConsideration: createNegotiationField(null),
      startConsiderationOverride: createNegotiationField(null),
      startDate: createNegotiationField(null),
      status: null,
      stockBorrowingLendingType: createNegotiationField(null),
      summaryEveryChildLegModified: false,
      summaryModified: false,
      summaryNegotiationId: null,
      targetFillNotional: null,
      targetFillStartConsideration: null,
      term: createNegotiationField(null),
      totalFillNotional: null,
      totalFillStartConsideration: null,
      tradeClassification: null,
      tradeConditionFlag: createNegotiationField(null),
      tradedAt: null,
      traderUserId: null,
      tradingType: null,
      uploadRowIndex: null,
      userIdWithLock: null,
      version: '',
      factor: null,
      assetType: null,
      evergreenNoticePeriod: null,
      userHasUpdatedNotionalOrCash: null,
      nestedLegs: null,
      collateralIssue: null,
      preAssigned: false,
      marketTradeDate: null,
      dateSource: null,
      indexRatio: null,
      rightOfExit: 1,
      rightOfSubstitution: 1,
      lastEvent: NegotiationEvent.REQUEST,
      lastTriggerEvent: NegotiationEvent.REQUEST,
      quotedAtLeastOnce: false,
      userModified: false
    },
    direction: null,
    clearingHouse: null,
    sponsor: null,
    counterpartySponsor: null,
    collection: items[0] ? items[0].containerCollection : null,
    containerCollection: items[0] ? items[0].containerCollection : null,
    collateral: null,
    collateralBasket: null,
    productSector: null,
    product: null,
    counterparty: null,
    counterpartyFirm: null,
    orderFund: null,
    orderFundFirm: null,
    referenceRate: null,
    floatCrystallisationDays: null,
    userWithLock: null,
    axeTradeCondition: { isAxed: false, tradeCondition: TradeConditionFlag.NONE },
    ownOrder: true,
    excludeFromBestPriceCalculations: true,
    otwDuration: null,
    validationIssues: [],
    availableEvents: [],
    initiatorExecutionDecisionMaker: null,
    initiatorInvestmentDecisionMaker: null,
    responderExecutionDecisionMaker: null,
    responderInvestmentDecisionMaker: null,
    fundPrimarySalesCoverUser: null,
    fundSecondarySalesCoverUser: null,
    counterpartyPrimarySalesCoverUser: null,
    counterpartySecondarySalesCoverUser: null,
    notes: null,
    secondaryInstrumentName: null,
    usSponsoredRelationship: false,
    linkedTradeId: null
  }
}
